import { React, useState, useEffect } from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    LinearProgress,
    TablePagination,
} from "@mui/material";
import { Helper } from "../../../tools/Helper";
import { api_Routes } from "../../../api_Routes";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useCenteredSnackbar } from "../../../tools/useCenteredSnackbar";

const NewOrderHistory = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { showCenteredPopup } = useCenteredSnackbar();
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [data, setData] = useState([]);
    const [history, setHistory] = useState([]);
    const [pageloading, setPageLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState("all");

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const action = (key) => (
        <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => closeSnackbar(key)}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    );

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const calculateDiscount = (discount, discount_type, price) => {
        const numDiscount = Number(discount) || 0;
        const numPrice = Number(price) || 0;
        switch (discount_type) {
            case "const":
                return numDiscount.toFixed(2);
            case "percentage":
                return ((numPrice * numDiscount) / 100).toFixed(2);
            default:
                return "0.00";
        }
    };

    useEffect(() => {
        Order_History();
    }, [selectedFilter, currentPage, rowsPerPage]);

    const Order_History = async () => {
        setPageLoading(true);
        let url = `${api_Routes.dashboard.orderhistory.unified}?per_page=${rowsPerPage}&page=${currentPage + 1}`;

        if (selectedFilter !== "all") {
            url += `&status=${selectedFilter}`;
        }

        const { response } = await Helper.Get({
            url,
            hasToken: true,
        });

        if (response) {
            setData(response);
            setHistory(response.data);
        }
        setPageLoading(false);
    };

    const handleFilterChange = (filter) => {
        setSelectedFilter(filter);
        setCurrentPage(0);
    };

    const handleDownload = async (report) => {
        try {
            const link = document.createElement("a");
            link.href = report;
            link.setAttribute("download", "image.jpg");
            link.setAttribute("target", "_blank");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            showCenteredPopup(error || "Failed to download report", {
                variant: "error",
                persist: true,
            });
        }
    };

    const getTypeBadge = (type) => {
        const typeConfig = {
            service: { label: "Service", color: "#3F78E0" },
            offer: { label: "Offer", color: "#ECA601" },
            domain: { label: "Domain", color: "#28a745" },
        };

        const config = typeConfig[type] || { label: type, color: "#6c757d" };

        return (
            <Box
                sx={{
                    backgroundColor: config.color,
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    display: "inline-block",
                }}
            >
                {config.label}
            </Box>
        );
    };

    const handleViewDetails = (item) => {
        switch (item.type) {
            case 'service':
                if ((item.ourServiceNameId === 1 || item.ourServiceNameId === 10 ||
                    item.ourServiceNameId === 11 || item.ourServiceNameId === 9) &&
                    (item.status === "in_progress" || item.status === "complete")) {
                    navigate(`/dashboard/order-history/service-statistic/${item.request_id}`);
                } else {
                    navigate(`/dashboard/order-history/service-details/${item.uuid}`);
                }
                break;
            case 'offer':
                navigate(`/dashboard/order-history/offer-details/${item.uuid}`);
                break;
            case 'domain':
                navigate(`/dashboard/order-history/domain-details/${item.uuid}`);
                break;
            default:
                break;
        }
    };

    const getActionButtons = (item) => {
        const buttons = [];

        // Report/Statistics button for services
        if (item.type === 'service') {
            if ((item.ourServiceNameId === 1 || item.ourServiceNameId === 10 ||
                item.ourServiceNameId === 11 || item.ourServiceNameId === 9) &&
                (item.status === "in_progress" || item.status === "complete")) {
                buttons.push(
                    <Button
                        key="statistics"
                        sx={{
                            background: "#ECA601",
                            boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                            color: "white",
                            borderRadius: "6px",
                            width: "85px",
                            fontSize: "12px",
                            margin: "2px",
                        }}
                        onClick={() => handleViewDetails(item)}
                    >
                        Statistics
                    </Button>
                );
            } else if (item.report) {
                buttons.push(
                    <Button
                        key="report"
                        sx={{
                            backgroundColor: "#ECA601",
                            boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                            color: "white",
                            borderRadius: "6px",
                            width: "85px",
                            fontSize: "12px",
                            margin: "2px",
                        }}
                        onClick={() => handleDownload(item.report)}
                    >
                        Report
                    </Button>
                );
            }
        }

        // Pay button for pending payments
        if (item.status_payment === "pending" && item.status !== "complete") {
            let invoiceRoute = '';
            switch (item.type) {
                case 'service':
                    invoiceRoute = `/ServicesInvoice?invoice_id=${item.uuid}`;
                    break;
                case 'offer':
                    invoiceRoute = `/OffersInvoice?invoice_id=${item.uuid}`;
                    break;
                case 'domain':
                    invoiceRoute = `/DomainInvoice?invoice_id=${item.uuid}`;
                    break;
            }

            buttons.push(
                <Button
                    key="pay"
                    sx={{
                        backgroundColor: "#3F78E0",
                        color: "#fff",
                        boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                        borderRadius: "6px",
                        width: "85px",
                        fontSize: "12px",
                        margin: "2px",
                    }}
                    onClick={() => navigate(invoiceRoute)}
                >
                    Pay
                </Button>
            );
        }

        // Edit button for services
        if (item.type === 'service' &&
            item.ourServiceNameId !== 1 &&
            item.ourServiceNameId !== 28 &&
            item.ourServiceNameId !== 40 &&
            item.ourServiceNameId !== 41 &&
            item.ourServiceNameId !== 42 &&
            item.ourServiceNameId !== 43 &&
            item.ourServiceNameId !== 44 &&
            item.ourServiceNameId !== 45 &&
            item.ourServiceNameId !== 46 &&
            item.status !== "complete") {
            buttons.push(
                <Button
                    key="edit"
                    sx={{
                        backgroundColor: "#3F78E0",
                        color: "#fff",
                        boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                        borderRadius: "6px",
                        width: "85px",
                        fontSize: "12px",
                        margin: "2px",
                    }}
                    onClick={() => navigate(`/dashboard/ServicesEdit/${item.uuid}`)}
                >
                    Edit
                </Button>
            );
        }

        // Re Order button for offers
        if (item.type === 'offer' && item.offerId) {
            buttons.push(
                <Button
                    key="reorder"
                    sx={{
                        backgroundColor: "#3F78E0",
                        color: "#fff",
                        boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                        borderRadius: "6px",
                        width: "85px",
                        fontSize: "12px",
                        margin: "2px",
                    }}
                    onClick={() => navigate(`/OffersDetails/${item.offerId}`)}
                >
                    Re Order
                </Button>
            );
        }

        // View Details button (always show)
        if (item.type !== 'domain'){
            buttons.push(
                <Button
                    key="details"
                    sx={{
                        backgroundColor: "#28a745",
                        color: "#fff",
                        boxShadow: `0px 3px 3px #00000040, inset 0px 4px 7px #FFFFFF40`,
                        borderRadius: "6px",
                        width: "85px",
                        fontSize: "12px",
                        margin: "2px",
                    }}
                    onClick={() => handleViewDetails(item)}
                >
                    Details
                </Button>
            );
        }


        return buttons;
    };

    return (
        <>
            <Box>
                <Typography
                    sx={{
                        color: "#474747",
                        fontSize: "24px",
                        lineHeight: "30px",
                        fontWeight: "600",
                    }}
                >
                    Your Orders
                </Typography>

                <Box
                    sx={{
                        display: { sm: "flex", xs: "grid" },
                        gridTemplateColumns: { sm: "auto", xs: "1fr 1fr" },
                        flexDirection: "row",
                        gap: "10px",
                        marginTop: "20px",
                    }}
                >
                    {["all", "pending", "processing", "complete","cancel"].map((filter) => (
                        <Button
                            key={filter}
                            variant={selectedFilter === filter ? "contained" : "outlined"}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#3F78E0",
                                    color: "white",
                                },
                                borderRadius: "5px",
                                border: "2px solid #3F78E0",
                                color: selectedFilter === filter ? "white" : "#303030",
                                fontSize: "14px",
                                fontWeight: "600",
                                paddingBlock: "10px",
                                lineHeight: "18px",
                                width: { sm: "130px", xs: "100%" },
                                backgroundColor:
                                    selectedFilter === filter ? "#3F78E0" : "white",
                            }}
                            onClick={() => handleFilterChange(filter)}
                        >
                            {filter.replace("_", " ")}
                        </Button>
                    ))}
                </Box>

                {pageloading && (
                    <Box sx={{ width: "100%", marginTop: "10px" }}>
                        <LinearProgress />
                    </Box>
                )}

                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: "none",
                        border: "2px solid #3F78E0",
                        marginTop: "20px",
                    }}
                >
                    {history?.length > 0 ? (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#D1DDE4" }}>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Type
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            ID
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Title/Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Description/Domain
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Status
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Payment Status
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Price
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Discount
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Expired At
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", color: "#333333", textAlign: "center", borderInlineEnd: "1px solid #3F78E0" }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                backgroundColor: "#E5EDFF",
                                                borderBlock: "2px solid #3F78E0",
                                            }}
                                        >
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0" }}>
                                                {getTypeBadge(row.type)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.request_id}<br/>
                                                {
                                                    row.created_at
                                                }
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.type === 'service' && row.ourServiceName}
                                                {row.type === 'offer' && (row.offerName || 'Custom Offer')}
                                                {row.type === 'domain' && 'Domain Registration'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.type === 'service' && row.description}
                                                {row.type === 'offer' && row.description}
                                                {row.type === 'domain' && row.domain}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.status}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.status_payment}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                ${row.price}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                ${calculateDiscount(row?.discount, row?.discount_type, row?.price)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", border: "1px solid #3F78E0", color: "#333333" }}>
                                                {row.expired_at}
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: "center",
                                                color: "#333333",
                                                border: "1px solid #3F78E0",
                                                minHeight: "80px",
                                                padding: "8px"
                                            }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        gap: "5px",
                                                        minHeight: "80px"
                                                    }}
                                                >
                                                    {getActionButtons(row)}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="mt-4 flex justify-center items-center">
                                <TablePagination
                                    page={currentPage}
                                    className="mt-auto"
                                    onPageChange={handlePageChange}
                                    count={data.pagination?.total || 0}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    variant="outlined"
                                    shape="rounded"
                                    rowsPerPageOptions={[25, 50, 100]}
                                />
                            </div>
                        </>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#D1DDE4" }}>
                                    <TableCell
                                        sx={{
                                            fontWeight: "bold",
                                            color: "#333333",
                                            textAlign: "center",
                                            border: "1px solid #3F78E0",
                                        }}
                                    >
                                        {pageloading ? "Loading..." : "There are no data yet"}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                        </Table>
                    )}
                </TableContainer>
            </Box>
        </>
    );
};

export default NewOrderHistory;